import React, { useState, useEffect } from 'react';
import { Search, FileText } from 'lucide-react';
import { SERVIDOR } from './../../../config';
import axios from 'axios';

const VerifiedUserPanel = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [allCards, setAllCards] = useState([]);
  const [displayedCards, setDisplayedCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value === '') {
      setDisplayedCards(allCards);
    } else {
      const filtered = allCards.filter(card =>
        card.description?.toLowerCase().includes(value.toLowerCase()) ||
        card.moldingNumber?.toLowerCase().includes(value.toLowerCase()) ||
        card.category?.toLowerCase().includes(value.toLowerCase())
      );
      setDisplayedCards(filtered);
    }
  };

  const handlePDFView = () => {
    window.location.href = '/pdfver';
  };

  const handleContact = () => {
    window.location.href = '/contact';
  };

  const validarAccesso = async () => {
    try {
      const token = localStorage.getItem('accesToken');
      if (!token) {
        throw new Error('No access token found');
      }

      const response = await axios.post(SERVIDOR + '/api/validar-acceso', { 
        accesToken: token 
      });

      if (!response.data.ok) {
        throw new Error('Access denied');
      }

      return true;
    } catch (error) {
      console.error('Error validating access:', error);
      alert('Acceso denegado');
      window.location.href = '/login';
      return false;
    }
  };

  const fetchCards = async () => {
    try {
      const response = await fetch(SERVIDOR + '/api/v2');
      if (!response.ok) {
        throw new Error('Failed to fetch cards');
      }
      const data = await response.json();
      return data.arreglo || [];
    } catch (error) {
      console.error('Error fetching cards:', error);
      throw error;
    }
  };

  useEffect(() => {
    const initializePanel = async () => {
      try {
        setIsLoading(true);
        const accessValid = await validarAccesso();
        
        if (accessValid) {
          const cardsData = await fetchCards();
          setAllCards(cardsData);
          setDisplayedCards(cardsData);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    initializePanel();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#697565]/10">
        <div className="text-lg text-[#697565]">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#697565]/10">
        <div className="text-lg text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-10 p-4 md:p-8 lg:p-24 min-h-screen bg-[#697565]/10 mt-20">
      {/* Main Content Section */}
      <div className="w-full lg:w-3/4 space-y-6">
        {/* Search Bar */}
        <div className="p-4 bg-white/80 backdrop-blur-sm border border-[#697565]/20 rounded-lg shadow-sm">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by description, number or category..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 border border-[#697565]/30 rounded-md 
                         focus:outline-none focus:ring-2 focus:ring-[#697565]/50 focus:border-[#697565]
                         placeholder-gray-400 text-gray-800"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-[#697565]" />
          </div>
        </div>

        {/* Results Counter */}
        <div className="text-sm text-[#697565]">
          Showing {displayedCards.length} of {allCards.length} items
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayedCards.length > 0 ? (
            displayedCards.map((card) => (
              <div 
                key={card.id}
                className="overflow-hidden hover:shadow-lg transition-all duration-300 
                          bg-white/80 backdrop-blur-sm border border-[#697565]/20 
                          hover:border-[#697565]/40 rounded-lg"
              >
                <img
                  src={card.image || "/api/placeholder/200/200"}
                  alt={card.description}
                  className="w-full h-48 object-cover"
                  onError={(e) => {
                    e.target.src = "/api/placeholder/200/200";
                  }}
                />
                <div className="p-4">
                  <h3 className="font-semibold mb-2 text-[#2D2F2B]">
                    {card.description}
                  </h3>
                  <p className="text-sm text-[#697565]">
                    Number: {card.moldingNumber}
                  </p>
                  <p className="text-sm text-[#697565]">
                    Category: {card.category}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-[#697565]">
              No items found matching your search.
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div className="w-full lg:w-1/4 mt-6 lg:mt-0">
        <div className="p-6 space-y-6 bg-gradient-to-br from-[#697565]/90 to-[#2D2F2B]/90 
                      backdrop-blur-sm border border-[#697565]/20 rounded-lg sticky top-24">
          <button
            onClick={handlePDFView}
            className="w-full h-16 flex items-center justify-center gap-2 
                     bg-white/10 hover:bg-white/20 transition-all duration-300 
                     border border-white/20 hover:border-white/30 text-white rounded-lg
                     focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            <FileText className="h-6 w-6" />
            <span>View PDF Catalog</span>
          </button>

          <button
            onClick={handleContact}
            className="w-full h-16 flex items-center justify-center gap-2 
                     bg-white/10 hover:bg-white/20 transition-all duration-300 
                     border border-white/20 hover:border-white/30 text-white rounded-lg
                     focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            <span>Contact</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifiedUserPanel;